import supabase from '@/services/supabaseClient';
import { callFunctionRaw } from '@/lib/callFunction';

class VideoConsultationService {
  async callManagedConsultationFunction(functionName, payload = {}, options = {}) {
    try {
      const data = await callFunctionRaw(functionName, payload, options);
      return { data, error: null };
    } catch (error) {
      console.error(`Error calling ${functionName}:`, error);
      return { data: null, error };
    }
  }

  async ensureAppointmentConsultationRoom(appointmentId, options = {}) {
    return this.callManagedConsultationFunction(
      'video-room-ensure',
      { appointmentId },
      options
    );
  }

  async getAppointmentJoinCredentials(appointmentId, options = {}) {
    return this.callManagedConsultationFunction(
      'video-token',
      { appointmentId },
      {
        preferFreshSession:
          options?.preferFreshSession === undefined
            ? true
            : Boolean(options.preferFreshSession),
        timeoutMs: Number(options?.timeoutMs || 30000),
        ...options,
      }
    );
  }

  async markAppointmentSessionStarted(appointmentId, state = {}, options = {}) {
    return this.callManagedConsultationFunction(
      'video-session-start',
      {
        appointmentId,
        connectionState: state?.connectionState || null,
        deviceState: state?.deviceState || null
      },
      options
    );
  }

  async markAppointmentSessionEnded(appointmentId, state = {}, options = {}) {
    return this.callManagedConsultationFunction(
      'video-session-end',
      {
        appointmentId,
        reason: state?.reason || null,
        finalize: state?.finalize !== false
      },
      options
    );
  }

  async persistAppointmentTranscriptLine(appointmentId, entry = {}, options = {}) {
    const content = String(entry?.content || '').trim();
    if (!appointmentId || !content) {
      return { data: null, error: null };
    }

    return this.callManagedConsultationFunction(
      'video-transcript-ingest',
      {
        appointmentId,
        speakerType: entry?.speakerType || null,
        speakerName: entry?.speakerName || null,
        content,
        confidenceScore: entry?.confidenceScore ?? null,
        timestampInSession: entry?.timestampInSession || null,
        providerEventId: entry?.providerEventId || null,
        provider: entry?.provider || 'twilio_video',
        rawEvent: entry?.rawEvent || null,
      },
      {
        timeoutMs: Number(options?.timeoutMs || 10000),
        ...options,
      }
    );
  }

  async getAppointmentSessionSnapshot(appointmentId) {
    try {
      const [{ data: appointment, error: appointmentError }, { data: session, error: sessionError }] = await Promise.all([
        supabase
          ?.from('appointments')
          ?.select(`
            id,
            video_provider,
            video_room_name,
            video_status,
            video_started_at,
            video_ended_at,
            video_recording_enabled,
            video_transcription_enabled
          `)
          ?.eq('id', appointmentId)
          ?.maybeSingle(),
        supabase
          ?.from('appointment_video_sessions')
          ?.select(`
            id,
            appointment_id,
            room_name,
            room_type,
            status,
            doctor_joined_at,
            patient_joined_at,
            started_at,
            ended_at,
            recording_status,
            recording_url,
            transcription_status,
            transcription_ref,
            last_webhook_event,
            updated_at
          `)
          ?.eq('appointment_id', appointmentId)
          ?.maybeSingle()
      ]);

      if (appointmentError && appointmentError?.code !== 'PGRST116') {
        console.error('Error fetching appointment session snapshot:', appointmentError);
        return { data: null, error: appointmentError };
      }

      if (sessionError && sessionError?.code !== 'PGRST116') {
        console.error('Error fetching appointment video session:', sessionError);
        return { data: null, error: sessionError };
      }

      return {
        data: {
          appointment: appointment || null,
          session: session || null
        },
        error: null
      };
    } catch (error) {
      console.error('Error in getAppointmentSessionSnapshot:', error);
      return { data: null, error };
    }
  }

  // Get consultation with all related data
  async getConsultation(consultationId) {
    try {
      const { data, error } = await supabase?.from('consultations')?.select(`
          id,
          appointment_id,
          status,
          session_started_at,
          session_ends_at,
          google_meet_link,
          recording_url,
          transcript_summary,
          appointments(
            id,
            patient_id,
            doctor_id,
            appointment_date,
            duration_minutes,
            status,
            consultation_type
          )
        `)?.eq('id', consultationId)?.single();

      if (error) {
        console.error('Error fetching consultation:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error in getConsultation:', error);
      return { data: null, error };
    }
  }

  // Get consultation settings
  async getConsultationSettings(consultationId) {
    try {
      const { data, error } = await supabase?.from('consultation_settings')?.select('*')?.eq('consultation_id', consultationId)?.single();

      if (error && error?.code !== 'PGRST116') {
        console.error('Error fetching consultation settings:', error);
        return { data: null, error };
      }

      return { data: data || null, error: null };
    } catch (error) {
      console.error('Error in getConsultationSettings:', error);
      return { data: null, error };
    }
  }

  // Create or update consultation settings
  async updateConsultationSettings(consultationId, settings) {
    try {
      const { data, error } = await supabase?.from('consultation_settings')?.upsert({
          consultation_id: consultationId,
          ...settings
        })?.select()?.single();

      if (error) {
        console.error('Error updating consultation settings:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error in updateConsultationSettings:', error);
      return { data: null, error };
    }
  }

  // Generate Google Meet link
  async generateGoogleMeetLink(consultationId) {
    try {
      // In a real implementation, this would integrate with Google Meet API
      const meetLink = `https://meet.google.com/${this.generateMeetId()}`;
      
      const { data, error } = await supabase?.from('consultations')?.update({ google_meet_link: meetLink })?.eq('id', consultationId)?.select()?.single();

      if (error) {
        console.error('Error updating Google Meet link:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error in generateGoogleMeetLink:', error);
      return { data: null, error };
    }
  }

  // Helper function to generate Meet ID
  generateMeetId() {
    const chars = 'abcdefghijklmnopqrstuvwxyz';
    const segments = [];
    for (let i = 0; i < 3; i++) {
      let segment = '';
      for (let j = 0; j < 4; j++) {
        segment += chars?.[Math.floor(Math.random() * chars?.length)];
      }
      segments?.push(segment);
    }
    return segments?.join('-');
  }

  // Get live transcript
  async getTranscript(consultationId) {
    try {
      const { data, error } = await supabase?.from('consultation_transcripts')?.select('*')?.eq('consultation_id', consultationId)?.order('timestamp_in_session', { ascending: true });

      if (error) {
        console.error('Error fetching transcript:', error);
        return { data: null, error };
      }

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error in getTranscript:', error);
      return { data: null, error };
    }
  }

  // Add transcript entry
  async addTranscriptEntry(consultationId, entry) {
    try {
      const { data, error } = await supabase?.from('consultation_transcripts')?.insert({
          consultation_id: consultationId,
          speaker_type: entry?.speakerType,
          speaker_name: entry?.speakerName,
          content: entry?.content,
          confidence_score: entry?.confidenceScore || 0.95
        })?.select()?.single();

      if (error) {
        console.error('Error adding transcript entry:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error in addTranscriptEntry:', error);
      return { data: null, error };
    }
  }

  // Generate consultation report
  async generateConsultationReport(consultationId, reportData) {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      
      const { data, error } = await supabase?.from('consultation_reports')?.insert({
          consultation_id: consultationId,
          generated_by: user?.user?.id,
          report_type: reportData?.reportType || 'consultation_summary',
          title: reportData?.title,
          content: reportData?.content || {},
          summary: reportData?.summary,
          recommendations: reportData?.recommendations,
          follow_up_required: reportData?.followUpRequired || false,
          follow_up_date: reportData?.followUpDate
        })?.select()?.single();

      if (error) {
        console.error('Error generating consultation report:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error in generateConsultationReport:', error);
      return { data: null, error };
    }
  }

  // Get consultation reports
  async getConsultationReports(consultationId) {
    try {
      const { data, error } = await supabase?.from('consultation_reports')?.select('*')?.eq('consultation_id', consultationId)?.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching consultation reports:', error);
        return { data: null, error };
      }

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error in getConsultationReports:', error);
      return { data: null, error };
    }
  }

  // Export transcript to PDF/CSV
  async exportTranscript(consultationId, format = 'pdf') {
    try {
      const { data: transcript } = await this.getTranscript(consultationId);
      const { data: consultation } = await this.getConsultation(consultationId);

      if (!transcript || !consultation) {
        throw new Error('Unable to fetch consultation data');
      }

      if (format === 'pdf') {
        return await this.exportToPDF(transcript, consultation);
      } else if (format === 'csv') {
        return await this.exportToCSV(transcript, consultation);
      }

      throw new Error('Unsupported export format');
    } catch (error) {
      console.error('Error in exportTranscript:', error);
      return { data: null, error };
    }
  }

  // Export to PDF using jsPDF
  async exportToPDF(transcript, consultation) {
    try {
      // This would require jsPDF library
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      // Add header
      doc?.setFontSize(16);
      doc?.text('Video Consultation Transcript', 20, 20);
      
      doc?.setFontSize(12);
      doc?.text(`Patient: ${consultation?.appointments?.patient_id || 'N/A'}`, 20, 35);
      doc?.text(`Date: ${new Date(consultation.appointments?.appointment_date || Date.now())?.toLocaleDateString()}`, 20, 45);
      doc?.text(`Duration: ${consultation?.appointments?.duration_minutes || 0} minutes`, 20, 55);
      
      // Add transcript content
      let yPosition = 70;
      const pageHeight = doc?.internal?.pageSize?.height;
      
      transcript?.forEach((entry) => {
        if (yPosition > pageHeight - 30) {
          doc?.addPage();
          yPosition = 20;
        }
        
        const timestamp = new Date(entry.timestamp_in_session)?.toLocaleTimeString();
        doc?.setFontSize(10);
        doc?.text(`[${timestamp}] ${entry?.speaker_name}:`, 20, yPosition);
        yPosition += 5;
        
        const lines = doc?.splitTextToSize(entry?.content, 170);
        doc?.text(lines, 25, yPosition);
        yPosition += lines?.length * 5 + 5;
      });
      
      // Return blob for download
      const blob = doc?.output('blob');
      return { data: blob, error: null };
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      return { data: null, error };
    }
  }

  // Export to CSV
  async exportToCSV(transcript, consultation) {
    try {
      const csvHeader = 'Timestamp,Speaker,Content,Confidence\n';
      const csvRows = transcript?.map(entry => {
        const timestamp = new Date(entry.timestamp_in_session)?.toISOString();
        const speaker = entry?.speaker_name?.replace(/"/g, '""');
        const content = entry?.content?.replace(/"/g, '""');
        return `"${timestamp}","${speaker}","${content}",${entry?.confidence_score}`;
      })?.join('\n');
      
      const csvContent = csvHeader + csvRows;
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      return { data: blob, error: null };
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      return { data: null, error };
    }
  }

  // Share consultation summary
  async shareConsultationSummary(consultationId, shareOptions) {
    try {
      const { data: report } = await this.getConsultationReports(consultationId);
      
      if (!report || report?.length === 0) {
        throw new Error('No consultation report found to share');
      }
      
      const latestReport = report?.[0];
      
      // Generate shareable link or send via email
      if (shareOptions?.method === 'email') {
        return await this.shareViaEmail(latestReport, shareOptions?.recipients);
      } else if (shareOptions?.method === 'link') {
        return await this.generateShareableLink(latestReport);
      }
      
      throw new Error('Unsupported sharing method');
    } catch (error) {
      console.error('Error in shareConsultationSummary:', error);
      return { data: null, error };
    }
  }

  // Share via email (placeholder implementation)
  async shareViaEmail(report, recipients) {
    try {
      // This would integrate with email service (e.g., Resend)
      console.log('Sharing report via email to:', recipients);
      console.log('Report:', report);
      
      // For now, return success
      return { data: { success: true, message: 'Report shared successfully' }, error: null };
    } catch (error) {
      console.error('Error sharing via email:', error);
      return { data: null, error };
    }
  }

  // Generate shareable link (placeholder implementation)
  async generateShareableLink(report) {
    try {
      // This would generate a secure shareable link
      const shareId = `share_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
      const shareableLink = `${window.location?.origin}/shared-report/${shareId}`;
      
      return { 
        data: { 
          shareableLink, 
          expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toISOString() // 7 days
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Error generating shareable link:', error);
      return { data: null, error };
    }
  }

  // Real-time transcript subscription
  subscribeToTranscript(consultationId, callback) {
    const channel = supabase?.channel(`consultation_transcript_${consultationId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'consultation_transcripts',
          filter: `consultation_id=eq.${consultationId}`
        },
        (payload) => {
          callback?.(payload?.new);
        }
      )?.subscribe();

    return channel;
  }

  // Unsubscribe from real-time updates
  unsubscribeFromTranscript(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
}

export default new VideoConsultationService();
